import React, { useState, useEffect, createContext } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import usePageTracking from '../hooks/usePageTracking';
import { 
  HomeIcon, 
  BriefcaseIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  SparklesIcon,
  ArrowLeftIcon,
  Bars3Icon,
  UserGroupIcon,
  ChartBarIcon,
  BellIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { 
  HomeIcon as HomeIconSolid, 
  BriefcaseIcon as BriefcaseIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  Bars3Icon as Bars3IconSolid,
  BellIcon as BellIconSolid
} from '@heroicons/react/24/solid';
import useAuthStore from '../store/authStore';
import axios from 'axios';
import VacationStatusBar from './VacationStatusBar';
import NotificationStatusBar from './NotificationStatusBar';

// Context for status bar heights
export const StatusBarContext = createContext({ height: 0 });

const Layout = () => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [statusBarHeight, setStatusBarHeight] = useState(0);
  const [hasOnlyCancelledProjects, setHasOnlyCancelledProjects] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // 페이지 추적 훅 사용
  usePageTracking();
  
  // 고객의 경우 취소된 프로젝트만 있는지 확인
  useEffect(() => {
    const checkProjects = async () => {
      if (user?.user_type === 'customer' || user?.user_type === 'vip_customer') {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get('/api/projects', {
            headers: { Authorization: `Bearer ${token}` }
          });
          const projects = response.data.projects || [];
          
          // 취소되지 않은 프로젝트가 하나도 없으면 true
          const hasOnlyCancelled = projects.length > 0 && 
                                   projects.every(p => p.status === 'cancelled');
          setHasOnlyCancelledProjects(hasOnlyCancelled);
        } catch (error) {
          console.error('프로젝트 확인 실패:', error);
        }
      }
    };
    
    checkProjects();
  }, [user, location.pathname]); // 경로 변경 시에도 다시 확인
  
  // 프로젝트 클릭 핸들러
  const handleProjectClick = async (e) => {
    e.preventDefault();
    
    // 고객 또는 VIP 고객인 경우에만 처리
    if (user?.user_type === 'customer' || user?.user_type === 'vip_customer') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/projects', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const projects = response.data.projects || [];
        
        // 프로젝트가 1개인 경우 바로 상세 페이지로 이동
        if (projects.length === 1) {
          navigate(`/projects/${projects[0].id}`);
        } else {
          // 프로젝트가 여러 개이거나 없는 경우 목록 페이지로 이동
          navigate('/projects');
        }
      } catch (error) {
        console.error('프로젝트 목록 조회 실패:', error);
        navigate('/projects');
      }
    } else {
      // 관리자나 소장인 경우 그냥 목록 페이지로 이동
      navigate('/projects');
    }
  };
  
  // 하단 네비게이션 항목 - 항상 5개만 표시
  let bottomNavigation = [];
  
  if (user?.user_type === 'admin') {
    // 관리자: 홈, 프로젝트, 내정보, 관리자, 더보기
    bottomNavigation = [
      {
        name: '홈',
        href: '/',
        icon: HomeIcon,
        iconSolid: HomeIconSolid
      },
      {
        name: '프로젝트',
        href: '/projects',
        icon: BriefcaseIcon,
        iconSolid: BriefcaseIconSolid,
        onClick: handleProjectClick
      },
      {
        name: '내정보',
        href: '/profile',
        icon: UserCircleIcon,
        iconSolid: UserCircleIconSolid
      },
      {
        name: '관리자',
        href: '/admin',
        icon: Cog6ToothIcon,
        iconSolid: Cog6ToothIconSolid
      },
      {
        name: '더보기',
        href: '#',
        icon: Bars3Icon,
        iconSolid: Bars3IconSolid,
        onClick: () => setShowMoreMenu(true)
      }
    ];
  } else if (user?.user_type === 'contractor') {
    // 소장: 홈, 프로젝트, 내 통계, 수수료, 더보기
    bottomNavigation = [
      {
        name: '홈',
        href: '/',
        icon: HomeIcon,
        iconSolid: HomeIconSolid
      },
      {
        name: '프로젝트',
        href: '/projects',
        icon: BriefcaseIcon,
        iconSolid: BriefcaseIconSolid,
        onClick: handleProjectClick
      },
      {
        name: '내 통계',
        href: '/contractor/stats',
        icon: ChartBarIcon,
        iconSolid: ChartBarIcon
      },
      {
        name: '수수료',
        href: '/contractor/commission',
        icon: CurrencyDollarIcon,
        iconSolid: CurrencyDollarIcon
      },
      {
        name: '더보기',
        href: '#',
        icon: Bars3Icon,
        iconSolid: Bars3IconSolid,
        onClick: () => setShowMoreMenu(true)
      }
    ];
  } else {
    // 고객/VIP: 홈, 프로젝트, 내정보, 소장님/공지사항, 더보기
    bottomNavigation = [
      {
        name: '홈',
        href: '/',
        icon: HomeIcon,
        iconSolid: HomeIconSolid
      },
      {
        name: '프로젝트',
        href: '/projects',
        icon: BriefcaseIcon,
        iconSolid: BriefcaseIconSolid,
        onClick: handleProjectClick
      },
      {
        name: '내정보',
        href: '/profile',
        icon: UserCircleIcon,
        iconSolid: UserCircleIconSolid
      },
      {
        name: hasOnlyCancelledProjects ? '공지사항' : '소장님',
        href: hasOnlyCancelledProjects ? '/notices' : '/my-contractor',
        icon: hasOnlyCancelledProjects ? BellIcon : UserGroupIcon,
        iconSolid: hasOnlyCancelledProjects ? BellIconSolid : UserGroupIcon
      },
      {
        name: '더보기',
        href: '#',
        icon: Bars3Icon,
        iconSolid: Bars3IconSolid,
        onClick: () => setShowMoreMenu(true)
      }
    ];
  }

  // 더보기 메뉴 항목
  const moreMenuItems = [
    {
      name: '공지사항',
      href: '/notices',
      icon: BellIcon,
      show: true
    },
    {
      name: 'VIP 리워드',
      href: '/vip',
      icon: SparklesIcon,
      show: user?.user_type === 'vip_customer'
    },
    {
      name: '관리자',
      href: '/admin',
      icon: Cog6ToothIcon,
      show: user?.user_type === 'admin'
    },
    {
      name: '내 프로필',
      href: '/profile',
      icon: UserCircleIcon,
      show: true
    },
    {
      name: '도움말',
      href: '/help',
      icon: QuestionMarkCircleIcon,
      show: true
    },
    {
      name: '이용약관',
      href: '/terms',
      icon: DocumentTextIcon,
      show: true
    },
    {
      name: '개인정보',
      href: '/privacy',
      icon: ShieldCheckIcon,
      show: true
    }
  ].filter(item => item.show);

  // 현재 페이지가 상세 페이지인지 확인
  const showBackButton = false; // 프로젝트 상세 헤더 숨김

  // 현재 활성화된 탭 찾기
  const currentTab = bottomNavigation.find(item => {
    if (item.href === '/') {
      return location.pathname === '/';
    }
    // /p/:id, /s/:id 경로도 프로젝트 탭 활성화
    if (item.href === '/projects') {
      return location.pathname.startsWith(item.href) || 
             location.pathname.startsWith('/p/') || 
             location.pathname.startsWith('/s/');
    }
    return location.pathname.startsWith(item.href);
  });

  // Calculate total status bar height
  useEffect(() => {
    const updateHeight = () => {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        const container = document.getElementById('status-bars-container');
        if (container) {
          setStatusBarHeight(container.offsetHeight);
        }
      }, 0);
    };

    // Update on mount and when window resizes
    updateHeight();
    window.addEventListener('resize', updateHeight);
    
    // Observe DOM changes
    const observer = new MutationObserver(updateHeight);
    setTimeout(() => {
      const container = document.getElementById('status-bars-container');
      if (container) {
        observer.observe(container, { childList: true, subtree: true, attributes: true });
      }
    }, 100);

    return () => {
      window.removeEventListener('resize', updateHeight);
      observer.disconnect();
    };
  }, [user]);

  return (
    <StatusBarContext.Provider value={{ height: statusBarHeight }}>
      <div className="min-h-screen bg-gray-50 pb-16">
        {/* 상태 바들 - 최상단에 위치 */}
        <div id="status-bars-container" className="sticky top-0 z-50">
          {/* 알림 중지 상태 바 */}
          <NotificationStatusBar />
          
          {/* 휴가 상태 바 */}
          {(user?.user_type === 'admin' || user?.user_type === 'contractor') && (
            <VacationStatusBar />
          )}
        </div>

      {/* 상단 헤더 (상세 페이지에서만 표시) */}
      {showBackButton && (
        <div className="bg-white border-b sticky top-0 z-20 backdrop-blur-ios">
          <div className="flex items-center px-4 h-14">
            <button
              onClick={() => navigate(-1)}
              className="btn-ghost p-2 -ml-2"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="ml-3 text-lg font-semibold text-gray-900 truncate">
              프로젝트 상세
            </h1>
          </div>
        </div>
      )}

      {/* 메인 콘텐츠 */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* 하단 네비게이션 바 - 토스/케이뱅크 스타일 */}
      <nav 
        className="fixed bottom-0 left-0 right-0 bg-white z-50 safe-bottom"
        style={{
          boxShadow: '0 -4px 20px rgba(0,0,0,0.08), 0 -2px 8px rgba(0,0,0,0.04)',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          overflow: 'hidden'
        }}
      >
        <div className="grid grid-cols-5 px-2 pt-0.5">
          {bottomNavigation.map((item) => {
            const isActive = currentTab?.href === item.href;
            const Icon = isActive ? item.iconSolid : item.icon;
            
            return item.onClick ? (
              <button
                key={item.name}
                onClick={item.onClick}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 transition-all group ${
                  isActive 
                    ? 'text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700 active:text-gray-800'
                }`}
              >
                {/* 활성 상태 배경 - 더 연한 색상 */}
                {isActive && (
                  <div className="absolute inset-x-1 inset-y-1 rounded-xl -z-10" 
                    style={{ 
                      backgroundColor: 'rgba(219, 234, 254, 0.3)', // 매우 연한 파란색
                      border: '1px solid rgba(147, 197, 253, 0.2)' // 연한 테두리
                    }} 
                  />
                )}
                
                {/* 아이콘 */}
                <Icon className={`h-6 w-6 transition-all ${isActive ? 'scale-105' : ''}`} />
                
                {/* 텍스트 */}
                <span className={`text-xs mt-0.5 font-medium transition-all ${
                  isActive ? 'font-semibold' : ''
                }`}>{item.name}</span>
              </button>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => {
                  // 페이지 전환 시 스크롤 위치 초기화
                  window.scrollTo({ top: 0 });
                }}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 transition-all group ${
                  isActive 
                    ? 'text-blue-600' 
                    : 'text-gray-500 hover:text-gray-700 active:text-gray-800'
                }`}
              >
                {/* 활성 상태 배경 - 더 연한 색상 */}
                {isActive && (
                  <div className="absolute inset-x-1 inset-y-1 rounded-xl -z-10" 
                    style={{ 
                      backgroundColor: 'rgba(219, 234, 254, 0.3)', // 매우 연한 파란색
                      border: '1px solid rgba(147, 197, 253, 0.2)' // 연한 테두리
                    }} 
                  />
                )}
                
                {/* 아이콘 */}
                <Icon className={`h-6 w-6 transition-all ${isActive ? 'scale-105' : ''}`} />
                
                {/* 텍스트 */}
                <span className={`text-xs mt-0.5 font-medium transition-all ${
                  isActive ? 'font-semibold' : ''
                }`}>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* 더보기 메뉴 */}
      {showMoreMenu && (
        <div className="fixed inset-0 z-50">
          {/* 배경 오버레이 */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMoreMenu(false)}
          />
          
          {/* 메뉴 패널 */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl transform transition-transform max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">메뉴</h2>
                <button
                  onClick={() => setShowMoreMenu(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto px-4 py-3">
              {/* 사용자 정보 */}
              <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <UserCircleIcon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-600">
                      {user?.user_type === 'admin' && '관리자'}
                      {user?.user_type === 'contractor' && user?.company_name}
                      {user?.user_type === 'customer' && '고객'}
                      {user?.user_type === 'vip_customer' && 'VIP 고객'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 메뉴 항목 */}
              <div className="space-y-1">
                {moreMenuItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => {
                      setShowMoreMenu(false);
                      // 페이지 전환 시 스크롤 위치 초기화
                      window.scrollTo({ top: 0 });
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    <item.icon className="h-5 w-5 text-gray-400" />
                    <span className="font-medium text-gray-700">{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* 로그아웃 버튼 - 항상 하단에 고정 */}
            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  setShowLogoutModal(true);
                }}
                className="flex items-center gap-3 px-4 py-3 w-full rounded-xl bg-white hover:bg-red-50 transition-colors text-red-600 shadow-sm"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="font-medium">로그아웃</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 로그아웃 확인 모달 */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl">
            <div className="p-6 text-center">
              <div className="relative">
                <div className="absolute inset-0 bg-red-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <ArrowRightOnRectangleIcon className="h-8 w-8 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">로그아웃 하시겠습니까?</h3>
              <p className="text-sm text-gray-500">
                다시 로그인하려면 계정 정보가 필요합니다.
              </p>
            </div>
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm border border-gray-200"
              >
                취소
              </button>
              <button
                onClick={() => {
                  logout();
                  navigate('/login');
                }}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors shadow-sm"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </StatusBarContext.Provider>
  );
};

export default Layout;